import { Component } from '@theme/component';

/**
 * Continuous editorial rail.
 *
 * The same idea as the Selected Clients rail on ahmedalmusawi.com - the
 * sequence is rendered more than once, so moving by exactly one sequence width
 * lands on an identical picture and the loop has no seam - with one change
 * that matters on a phone: the motion is the rail's own scroll position, not a
 * CSS transform. The rail is a real scroll container, so a finger, a trackpad
 * or the keyboard moves it natively, with the browser's own momentum, and the
 * drift simply picks up again from wherever the reader left it.
 *
 * Layout is [copy][original][copy]. The original starts in view; the copies
 * are `inert` and `aria-hidden`, so every testimonial is announced once.
 * Without script the copies stay hidden and the rail is a plain scrollable
 * strip. With reduced motion it is the same strip plus previous/next controls,
 * and it never moves on its own.
 *
 * Positions are normalised - distance travelled from the start edge, positive
 * in both writing directions - because `scrollLeft` runs negative in RTL.
 */

/** How long the rail waits after a touch, scroll or button press before drifting again. */
const RESUME_DELAY = 2600;

/** How long the rail rests, aligned, the first time a reader actually sees it. */
const FIRST_VIEW_DWELL = 1600;

/** Share of the rail that must be on screen before it counts as seen. */
const SEEN_RATIO = 0.4;

/** Time constant of the ease into and out of motion, in seconds. */
const EASE = 0.32;

class AsxMarquee extends Component {
  requiredRefs = ['viewport', 'set'];

  #abort = new AbortController();
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  /** Normalised scroll position, kept as a float so slow speeds stay smooth. */
  #position = 0;
  /** The last position this component wrote, to tell its own scrolling apart from the reader's. */
  #written = Number.NaN;
  #velocity = 0;
  #setWidth = 0;
  #rtl = false;
  #auto = false;

  #frame = 0;
  #lastTime = 0;
  #resumeTimer = 0;

  #hovered = false;
  #focused = false;
  #pointerDown = false;
  #holdUntil = 0;
  #pausedByUser = false;
  #editorHold = false;
  #inView = false;
  #seen = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  connectedCallback() {
    super.connectedCallback();

    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    const signal = this.#abort.signal;

    viewport.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    viewport.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    viewport.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    viewport.addEventListener('pointerdown', this.#onPointerDown, { passive: true, signal });
    window.addEventListener('pointerup', this.#onPointerUp, { passive: true, signal });
    window.addEventListener('pointercancel', this.#onPointerUp, { passive: true, signal });
    viewport.addEventListener('focusin', this.#onFocusIn, { signal });
    viewport.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#update, { signal });
    this.#reducedMotion.addEventListener('change', this.#setup, { signal });

    // Theme Editor: selecting a testimonial holds the rail and brings the card
    // into view, so it is not drifting away while it is being edited.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(viewport);
    if (this.refs.set instanceof HTMLElement) this.#resizeObserver.observe(this.refs.set);

    // The rail only starts once enough of it is on screen to be read, and the
    // first time that happens it rests where it loaded - first card on the
    // gutter - so nobody arrives to find it already sliding out of frame.
    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (!entry.isIntersecting) {
          this.#inView = false;
        } else if (entry.intersectionRatio >= SEEN_RATIO) {
          this.#inView = true;
          if (!this.#seen) {
            this.#seen = true;
            if (this.#auto) this.#holdFor(FIRST_VIEW_DWELL);
          }
        }
        this.#update();
      },
      { threshold: [0, SEEN_RATIO] }
    );
    this.#intersectionObserver.observe(this);

    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#resumeTimer);
    this.#frame = 0;
  }

  /* ------------------------------------------------------------------ *
   * Mode
   * ------------------------------------------------------------------ */

  /**
   * Decides between the drifting rail and the manual strip, and lays the
   * copies out to match. Runs again if the reduced-motion preference changes
   * while the page is open.
   */
  #setup = () => {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    this.#rtl = getComputedStyle(viewport).direction === 'rtl';

    const wantsAuto = !this.#reducedMotion.matches;
    const offset = this.#auto ? this.#position - this.#setWidth : this.#readPosition();

    for (const clone of this.#clones) clone.hidden = !wantsAuto;
    this.#auto = wantsAuto;
    this.#measure();

    // If the set is not wider than the rail, a seamless loop is impossible;
    // the rail stays a manual strip rather than showing its seam.
    if (this.#auto && this.#setWidth < viewport.clientWidth + 1) {
      for (const clone of this.#clones) clone.hidden = true;
      this.#auto = false;
      this.#measure();
    }

    this.dataset.mode = this.#auto ? 'auto' : 'manual';

    // Revealing the leading copy pushes the original along by exactly one set.
    // Scrolling by the same amount in the same frame keeps the original where
    // it was, so switching modes never moves what the reader is looking at.
    // On first run the original's first card is set on the content gutter, the
    // same line the heading starts on, rather than hard against the glass.
    const firstRun = Number.isNaN(this.#written);
    const inset = firstRun && this.#auto ? this.#inset : 0;
    this.#position = this.#auto ? this.#setWidth + Math.max(0, offset) - inset : Math.max(0, offset);
    this.#velocity = 0;
    this.#write();

    const toggle = this.refs.toggle;
    if (toggle instanceof HTMLElement) toggle.hidden = !this.#auto;
    for (const control of this.querySelectorAll('[data-marquee-control]')) {
      if (control !== toggle && control instanceof HTMLElement) control.hidden = false;
    }
    this.#syncToggle();
    this.#update();
  };

  /** Width of one full sequence, including the gap that follows it. */
  #measure() {
    const set = this.refs.set;
    const viewport = this.refs.viewport;
    if (!(set instanceof HTMLElement) || !(viewport instanceof HTMLElement)) return;

    const previous = this.#setWidth;
    const gap = parseFloat(getComputedStyle(set.parentElement ?? set).columnGap) || 0;
    this.#setWidth = set.getBoundingClientRect().width + gap;

    // A resize changes every card's width. Keeping the same fraction of the
    // way through the sequence keeps the same card under the reader's eye.
    if (this.#auto && previous > 0 && this.#setWidth > 0 && previous !== this.#setWidth) {
      const phase = (this.#position - previous) / previous;
      this.#position = this.#setWidth + phase * this.#setWidth;
      this.#wrap();
      this.#write();
    }
  }

  get #clones() {
    const clones = this.refs.clones;
    return Array.isArray(clones) ? clones.filter((el) => el instanceof HTMLElement) : [];
  }

  /** The content gutter, read from the viewport's scroll padding. */
  get #inset() {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return 0;
    return parseFloat(getComputedStyle(viewport).scrollPaddingInlineStart) || 0;
  }

  get #speed() {
    return Math.max(4, Number(this.dataset.speed) || 24);
  }

  /* ------------------------------------------------------------------ *
   * Motion
   * ------------------------------------------------------------------ */

  get #shouldDrift() {
    return (
      this.#auto &&
      this.#inView &&
      !document.hidden &&
      !this.#pausedByUser &&
      !this.#editorHold &&
      !this.#hovered &&
      !this.#focused &&
      !this.#pointerDown &&
      performance.now() >= this.#holdUntil
    );
  }

  /** Starts the frame loop if there is anything for it to do. */
  #update = () => {
    if (this.#frame) return;
    if (!this.#shouldDrift && this.#velocity === 0) return;
    this.#lastTime = 0;
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** @param {number} now */
  #tick = (now) => {
    this.#frame = 0;
    const seconds = this.#lastTime ? Math.min(0.064, (now - this.#lastTime) / 1000) : 0;
    this.#lastTime = now;

    // Ease toward the target speed instead of switching, so hovering settles
    // the rail to a stop and leaving lets it gather pace again.
    const target = this.#shouldDrift ? this.#speed : 0;
    this.#velocity += (target - this.#velocity) * (1 - Math.exp(-seconds / EASE));
    if (target === 0 && this.#velocity < 0.5) this.#velocity = 0;

    if (this.#velocity > 0 && !this.#pointerDown) {
      this.#position += this.#velocity * seconds;
      this.#wrap();
      this.#write();
    }

    if (this.#velocity > 0 || target > 0) {
      this.#frame = requestAnimationFrame(this.#tick);
    }
  };

  /** Keeps the position inside the middle sequence. Every jump is exactly one set, so it is invisible. */
  #wrap() {
    if (!this.#auto || this.#setWidth <= 0) return;
    while (this.#position >= this.#setWidth * 2) this.#position -= this.#setWidth;
    while (this.#position < this.#setWidth) this.#position += this.#setWidth;
  }

  #readPosition() {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return 0;
    return this.#rtl ? -viewport.scrollLeft : viewport.scrollLeft;
  }

  #write() {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;
    viewport.scrollLeft = this.#rtl ? -this.#position : this.#position;
    this.#written = this.#position;
  }

  /** Holds the rail still for a moment after the reader has moved it. */
  #hold() {
    this.#holdFor(RESUME_DELAY);
  }

  /** @param {number} duration */
  #holdFor(duration) {
    this.#velocity = 0;
    this.#holdUntil = Math.max(this.#holdUntil, performance.now() + duration);
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => {
      // Momentum has finished by now, so this is the safe moment to bring the
      // position back into the middle sequence.
      if (!this.#pointerDown) {
        this.#position = this.#readPosition();
        this.#wrap();
        this.#write();
      }
      this.#update();
    }, this.#holdUntil - performance.now() + 20);
  }

  /* ------------------------------------------------------------------ *
   * Input
   * ------------------------------------------------------------------ */

  #onScroll = () => {
    const actual = this.#readPosition();
    // Writes land on whole or device pixels, so this component's own scrolling
    // always reads back within a pixel. Anything further is the reader.
    if (Number.isNaN(this.#written) || Math.abs(actual - this.#written) > 1.5) {
      this.#position = actual;
      this.#written = actual;
      if (this.#auto) this.#hold();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#update();
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = false;
    this.#update();
  };

  #onPointerDown = () => {
    this.#pointerDown = true;
    this.#velocity = 0;
  };

  #onPointerUp = () => {
    if (!this.#pointerDown) return;
    this.#pointerDown = false;
    if (this.#auto) this.#hold();
  };

  /** Keyboard focus holds the rail; a mouse click that happens to focus it does not. */
  #onFocusIn = (/** @type {FocusEvent} */ event) => {
    if (event.target instanceof HTMLElement && !event.target.matches(':focus-visible')) return;
    this.#focused = true;
  };

  #onFocusOut = (/** @type {FocusEvent} */ event) => {
    const viewport = this.refs.viewport;
    if (viewport instanceof HTMLElement && viewport.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.#focused = false;
    this.#update();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    const set = this.refs.set;
    if (!target || !(set instanceof HTMLElement) || !set.contains(target)) return;
    this.#editorHold = true;
    this.#velocity = 0;
    this.#scrollToCard(target, 'auto');
  };

  #onBlockDeselect = () => {
    if (!this.#editorHold) return;
    this.#editorHold = false;
    this.#update();
  };

  /* ------------------------------------------------------------------ *
   * Controls - bound declaratively from the markup
   * ------------------------------------------------------------------ */

  previous = () => this.#step(-1);

  next = () => this.#step(1);

  toggle = () => {
    this.#pausedByUser = !this.#pausedByUser;
    this.#syncToggle();
    this.#update();
  };

  #syncToggle() {
    const toggle = this.refs.toggle;
    if (!(toggle instanceof HTMLElement)) return;
    this.toggleAttribute('data-paused', this.#pausedByUser);
    const label = this.#pausedByUser ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /** Moves to the start of the next or previous card, aligned to the content edge. */
  #step(/** @type {number} */ direction) {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    if (this.#auto) {
      this.#hold();
      this.#position = this.#readPosition();
      this.#wrap();
      this.#write();
    }

    const inset = this.#inset;
    const here = this.#readPosition() + inset;
    const items = [...viewport.querySelectorAll('[data-marquee-item]')].filter(
      (item) => item instanceof HTMLElement && item.offsetParent !== null
    );
    const starts = items.map((item) => this.#startOf(/** @type {HTMLElement} */ (item))).sort((a, b) => a - b);

    // A card already sitting almost on the gutter does not count as the next
    // one - a press should always travel at least half a card.
    const minimum = Math.max(24, (items[0]?.getBoundingClientRect().width ?? 0) / 2);
    const target =
      direction > 0
        ? starts.find((start) => start > here + minimum)
        : starts.reverse().find((start) => start < here - minimum);
    if (target === undefined) return;

    this.#scrollTo(target - inset, 'smooth');
  }

  /** @param {HTMLElement} card @param {ScrollBehavior} behavior */
  #scrollToCard(card, behavior) {
    const viewport = this.refs.viewport;
    if (!(viewport instanceof HTMLElement)) return;
    const item = card.closest('[data-marquee-item]');
    if (!(item instanceof HTMLElement)) return;
    this.#scrollTo(this.#startOf(item) - this.#inset, behavior);
  }

  /** Normalised start edge of an item, whichever direction the page reads. */
  #startOf(/** @type {HTMLElement} */ item) {
    const viewport = /** @type {HTMLElement} */ (this.refs.viewport);
    const box = viewport.getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    const fromEdge = this.#rtl ? box.right - rect.right : rect.left - box.left;
    return this.#readPosition() + fromEdge;
  }

  /** @param {number} position @param {ScrollBehavior} behavior */
  #scrollTo(position, behavior) {
    const viewport = /** @type {HTMLElement} */ (this.refs.viewport);
    const instant = behavior === 'auto' || this.#reducedMotion.matches;
    const left = this.#rtl ? -position : position;
    viewport.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('asx-marquee')) {
  customElements.define('asx-marquee', AsxMarquee);
}

export default AsxMarquee;
